import { Box, Button, Flex, IllustratedMessage, notify, Pagination } from '@eleveo/components-react';
import { SearchNoneIcon } from '@eleveo/icons-react';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch, useSearchParams } from 'react-router-dom';

import { defaultProps, INITIAL_PAGE, PAGE_PARAM_KEY, PAGE_SIZE_PARAM_KEY } from '../../api/integrationsQuery';
import { ETLConfigurationPage } from '../../api/types';
import { useAuth } from '../../app/AuthorizationProvider';
import { Center } from '../../components/Center';
import { ConfigurationCard } from '../../components/configuration/ConfigurationCard';
import { ETLConfiguration, SUPPORTED_INTEGRATIONS } from '../../components/configuration/types';
import { TEST_IDS } from '../../constants/dataTestIds';
import { useSearchNavigate } from '../../hooks/useSearchNavigate';
import {
  createPickNewConfigurationUrl,
  PATH_MAIN_ROUTE,
  PATH_MY_CONFIGURATIONS,
  PATH_NEW_CONFIGURATION,
} from '../../router/routes';
import { ETL_PERMISSIONS } from '../../utils/keycloakUtils';

export const MyIntegrationsTopBar: React.FC = () => {
  const { t } = useTranslation();
  const { userHasPermissions } = useAuth();
  const { navigateToNewIntegrationPicker } = useSearchNavigate();

  const pickNewConfigurationMatch = useMatch(createPickNewConfigurationUrl());
  const createNewConfigurationMatch = useMatch(
    `${PATH_MAIN_ROUTE}${PATH_MY_CONFIGURATIONS}/${PATH_NEW_CONFIGURATION}/:integrationType`
  );

  const hasPermissions = useMemo(() => userHasPermissions([ETL_PERMISSIONS.createConfiguration]), [userHasPermissions]);

  const handleClick = () => {
    if (!hasPermissions) {
      notify.warning(t('missingPermissions'));
      return;
    }

    navigateToNewIntegrationPicker();
  };

  const isSelected = !!pickNewConfigurationMatch || !!createNewConfigurationMatch;

  return (
    <Button onClick={handleClick} isSelected={isSelected} testId={TEST_IDS.buttonNewIntegration} className="mis-auto">
      {t('myConfigurations.create')}
    </Button>
  );
};

interface MyIntegrationsContentProps {
  integrations: ETLConfigurationPage;
  isLoading: boolean;
}

export const MyIntegrationsContent: React.FC<MyIntegrationsContentProps> = ({ integrations, isLoading }) => {
  const { t } = useTranslation();
  const { navigateToEditIntegration } = useSearchNavigate();

  const handleEditNavigation = (configuration: ETLConfiguration) => {
    if ((SUPPORTED_INTEGRATIONS as readonly string[]).includes(configuration.integration.toLowerCase())) {
      navigateToEditIntegration(configuration);
      return;
    }

    if (process.env.NODE_ENV !== 'production') {
      notify.warning('Unsupported operation!');
    }
  };

  const { content }: ETLConfigurationPage = integrations;
  const hasData = !!content.length;

  return (
    <>
      {!hasData && !isLoading && (
        <Center>
          <IllustratedMessage
            heading={t('myConfigurations.noData.title')}
            description={t('myConfigurations.noData.description')}
            icon={<SearchNoneIcon />}
            testId={TEST_IDS.illustratedMessageNoData}
          />
        </Center>
      )}
      {hasData && (
        <Flex wrap justifyContent="start" alignItems="start" gap={40} testId={TEST_IDS.myIntegrationsScene}>
          {content.map((configuration) => (
            <ConfigurationCard<ETLConfiguration>
              key={configuration.id}
              configuration={configuration}
              onClick={handleEditNavigation}
              testId={TEST_IDS.buttonConfigurationCard(configuration.id)}
            />
          ))}
        </Flex>
      )}
    </>
  );
};

interface MyIntegrationsBottomBarProps {
  integrations: ETLConfigurationPage;
}

export const MyIntegrationsBottomBar: React.FC<MyIntegrationsBottomBarProps> = ({ integrations }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const newPage = integrations.page > integrations.totalPages ? INITIAL_PAGE : integrations.page + 1;
    searchParams.set(PAGE_PARAM_KEY, String(newPage));
    searchParams.set(PAGE_SIZE_PARAM_KEY, String(integrations.pageSize));

    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrations]);

  return (
    <Box padding={15}>
      <Pagination
        totalItems={integrations.total}
        selectedPageSize={integrations.pageSize}
        selectedPage={integrations.page + 1}
        pageSizes={defaultProps.pageSizes}
        onPageChange={(page, pageSize) => {
          searchParams.set(PAGE_PARAM_KEY, String(page));
          searchParams.set(PAGE_SIZE_PARAM_KEY, String(pageSize));

          setSearchParams(searchParams, { replace: true });
        }}
        onPageSizeChange={(pageSize) => {
          searchParams.set(PAGE_SIZE_PARAM_KEY, String(pageSize));

          setSearchParams(searchParams, { replace: true });
        }}
        testId={TEST_IDS.paginationIntegrations}
      />
    </Box>
  );
};
